import React, { useState, useEffect, useCallback } from 'react';
import { fetchAndCacheSuppliers, clearSuppliersCache } from '../data/suppliers';
import { useTheme } from '../contexts/ThemeContext';

// Google Sign-In types
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: (momentListener?: (promptMomentNotification: any) => void) => void;
          renderButton: (parent: HTMLElement, options: any) => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

interface Supplier {
  _id: string;
  name: string;
}

interface LoginProps {
  onLogin: (email: string, supplier?: string) => Promise<boolean>;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  // Step management
  const [currentStep, setCurrentStep] = useState<'auth' | 'config'>('auth');
  const [authenticatedEmail, setAuthenticatedEmail] = useState('');
  const [finalLoading, setFinalLoading] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Authentication state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [googleScriptLoaded, setGoogleScriptLoaded] = useState(false);
  
  // Configuration state
  const [supplierName, setSupplierName] = useState('');
  const [environment] = useState('testing'); // Hardcoded to testing environment
  const [googleInitialized, setGoogleInitialized] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [isSupplierDropdownOpen, setIsSupplierDropdownOpen] = useState(false);
  const [filteredSuppliers, setFilteredSuppliers] = useState<Supplier[]>([]);
  const [loadingStep, setLoadingStep] = useState('');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [lastUsedSupplier, setLastUsedSupplier] = useState<string | null>(null);
  
  // Use global theme context
  const { isDarkMode, toggleDarkMode } = useTheme();



  // Computed values for cleaner validation
  const isSupplierSelected = Boolean(supplierName && supplierName.trim().length > 0);

  // Function to get API base URL based on environment
  const getApiBaseUrl = (env: string) => {
    let baseUrl;
    switch (env) {
      case 'localhost':
      case 'development':
        baseUrl = 'http://localhost:8008';
        break;
      case 'testing':
        baseUrl = 'https://be-test.dev.qaraservices.net';
        break;
      case 'staging':
        baseUrl = 'https://hse-staging.dev.qaraservices.net';
        break;
      case 'production':
        baseUrl = 'https://sweedypro.dev.qaraservicesproduction.com';
        break;
      default:
        baseUrl = 'http://localhost:8008';
    }
    return baseUrl;
  };

  // Function to fetch suppliers from API
  const fetchSuppliers = async () => {
    setLoadingSuppliers(true);
    try {
      const data = await fetchAndCacheSuppliers(environment);
      setSuppliers(data);
      setError(''); // Clear any previous errors
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setError('Failed to load suppliers. Please try again.');
      // Set empty array if API fails
      setSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  // Create a stable callback that doesn't depend on state
  const handleGoogleResponse = useCallback(async (response: any) => {
    setLoading(true);
    setError('');
    
    try {
      if (!response || !response.credential) {
        throw new Error('Invalid Google response');
      }

      // Decode the JWT token to get user info
      const payload = JSON.parse(atob(response.credential.split('.')[1]));
      const email = payload.email;
      
      if (!email) {
        throw new Error('No email found in Google response');
      }

      // Validate email against Google Cloud IAM permissions
      const hasAccess = await checkGoogleCloudAccess(email, environment);
      if (!hasAccess) {
        setError(`Access denied for ${email}. Please contact your administrator to grant access in Google Cloud Console.`);
        setLoading(false);
        return;
      }

      // Store authenticated email and move to config step
      setAuthenticatedEmail(email);
      setCurrentStep('config');
    } catch (err: any) {
      setError('Google login failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [onLogin, suppliers]);

  // Function to check if user has access via Google Cloud IAM
  const checkGoogleCloudAccess = async (email: string, env?: string): Promise<boolean> => {
    try {
      // Get API URL based on selected environment
      const apiUrl = getApiBaseUrl(env || environment);
      
      // Call your backend API to verify IAM permissions
      const response = await fetch(`${apiUrl}/auth/check-iam-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email,
          projectId: 'formal-precinct-452707-r2' // Your Google Cloud project ID
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to check access permissions');
      }

      const data = await response.json();
      return data.hasAccess;
    } catch (error) {
      console.error('Error checking Google Cloud access:', error);
      // For development/testing purposes, you might want to return true
      // Remove this in production
      console.warn('Falling back to allow access due to API error');
      return false;
    }
  };

  // Handle final login after configuration
  const handleFinalLogin = async () => {
    // Prevent any interference by immediately setting both loading states
    setIsTransitioning(true);
    setFinalLoading(true);
    setError('');
    setLoadingProgress(0);
    setLoadingStep('Initializing...');

    // Force a synchronous state update
    await new Promise(resolve => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });

    try {
      // Step 1: Save preferences
      setLoadingStep('Saving preferences...');
      setLoadingProgress(20);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Store environment selection
      localStorage.setItem('selectedEnvironment', environment);
      
      // Save last used supplier
      if (supplierName) {
        localStorage.setItem('lastUsedSupplier', supplierName);
        localStorage.setItem('excel_supplier_name', supplierName);
      }

      // Step 2: Validate supplier
      setLoadingStep('Validating supplier access...');
      setLoadingProgress(40);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Convert supplier name to ID
      const supplier = suppliers.find(s => s.name === supplierName);
      const supplierId = supplier?._id;
      
      if (!supplierId) {
        throw new Error('Invalid supplier selected');
      }

      // Step 3: Establishing connection
      setLoadingStep('Establishing secure connection...');
      setLoadingProgress(60);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Step 4: Loading dashboard
      setLoadingStep('Loading dashboard...');
      setLoadingProgress(80);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Step 5: Final setup
      setLoadingStep('Finalizing setup...');
      setLoadingProgress(100);
      
      // Proceed to dashboard - onLogin should handle navigation
      await onLogin(authenticatedEmail, supplierId);
      // Don't reset finalLoading here - let the component unmount naturally
      
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Login failed. Please try again.');
      setFinalLoading(false); // Only reset loading on error
      setIsTransitioning(false);
      setLoadingProgress(0);
      setLoadingStep('');
    }
  };

  // Fetch suppliers when step changes to config
  useEffect(() => {
    if (currentStep === 'config') {
      clearSuppliersCache(); // Clear cache when environment changes
      fetchSuppliers();
    }
  }, [currentStep, environment]);

  // Original fetch suppliers function for backward compatibility
  useEffect(() => {
    clearSuppliersCache(); // Clear cache when environment changes
    fetchSuppliers();
  }, [environment]);

  // Filter suppliers based on search term - show all when dropdown open, filter when typing
  useEffect(() => {
    // If dropdown is closed and no search term, show empty list
    if (!isSupplierDropdownOpen && !supplierSearch.trim()) {
      setFilteredSuppliers([]);
      return;
    }
    
    // If dropdown is open but no search term, show all suppliers
    if (isSupplierDropdownOpen && !supplierSearch.trim()) {
      const allFiltered = suppliers
        .filter((supplier) => isNaN(Number(supplier.name)))
        .sort((a, b) => {
          // Sort last used supplier to the top
          if (a.name === lastUsedSupplier) return -1;
          if (b.name === lastUsedSupplier) return 1;
          return a.name.localeCompare(b.name);
        });
      setFilteredSuppliers(allFiltered);
      return;
    }
    
    // Filter based on search term
    const filtered = suppliers
      .filter((supplier) => isNaN(Number(supplier.name)))
      .filter((supplier) => 
        supplier.name.toLowerCase().includes(supplierSearch.toLowerCase())
      )
      .sort((a, b) => {
        // Sort last used supplier to the top
        if (a.name === lastUsedSupplier) return -1;
        if (b.name === lastUsedSupplier) return 1;
        return a.name.localeCompare(b.name);
      });
    setFilteredSuppliers(filtered);
  }, [suppliers, supplierSearch, lastUsedSupplier, isSupplierDropdownOpen]);

  // Load last used supplier from localStorage
  useEffect(() => {
    const savedSupplier = localStorage.getItem('lastUsedSupplier');
    if (savedSupplier) {
      setLastUsedSupplier(savedSupplier);
      // Auto-select if moving to config step and no supplier selected yet
      // if (currentStep === 'config' && !supplierName && suppliers.length > 0&& !supplierSearch) {
      //   const matchingSupplier = suppliers.find(s => s.name === savedSupplier);
      //   if (matchingSupplier) {
      //     setSupplierName(savedSupplier);
      //     setSupplierSearch(savedSupplier);
      //   }
      // }
      // Don't auto-select supplier - let user choose manually
    }
  }, [currentStep, suppliers, supplierName]);

  // Load Google Sign-In script
  useEffect(() => {
    const loadGoogleScript = () => {
      if (document.getElementById('google-signin-script')) {
        setGoogleScriptLoaded(true);
        setTimeout(initializeGoogleSignIn, 100);
        return;
      }

      const script = document.createElement('script');
      script.id = 'google-signin-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setGoogleScriptLoaded(true);
        setTimeout(initializeGoogleSignIn, 100);
      };
      script.onerror = () => {
        setError('Failed to load Google Sign-In. Please check your internet connection and try refreshing the page.');
      };
      document.head.appendChild(script);
    };

    loadGoogleScript();
  }, []);

  const initializeGoogleSignIn = useCallback(() => {
    if (window.google && window.google.accounts && window.google.accounts.id && !googleInitialized) {
      try {
        // Disable auto select to prevent unwanted popups
        window.google.accounts.id.disableAutoSelect();
        
        const config = {
          client_id: '545752863447-0l2d94rfc8bh8q8e21n466ebg2ff6cc6.apps.googleusercontent.com',
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: false,
        };
        
        window.google.accounts.id.initialize(config);
        setGoogleInitialized(true);
      } catch (error) {
        setError('Google Sign-In initialization failed. Please try refreshing the page.');
      }
    }
  }, [handleGoogleResponse, googleInitialized]);

  // Initialize Google Sign-In only once when script loads
  useEffect(() => {
    if (googleScriptLoaded && !googleInitialized) {
      setTimeout(initializeGoogleSignIn, 100);
    }
  }, [googleScriptLoaded, googleInitialized, initializeGoogleSignIn]);

  const handleGoogleLogin = useCallback(async () => {
    if (!googleScriptLoaded || !window.google || !window.google.accounts || !window.google.accounts.id) {
      setError('Google Sign-In is not ready. Please try again.');
      return;
    }

    setError('');
    
    try {
      // Only prompt when user explicitly clicks the button
      window.google.accounts.id.prompt((notification: any) => {
        if (notification && notification.isNotDisplayed && notification.isNotDisplayed()) {
          // Fallback to rendered button if prompt doesn't show
          renderGoogleButton();
        } else if (notification && notification.isSkippedMoment && notification.isSkippedMoment()) {
          // User dismissed the prompt, show fallback button
          renderGoogleButton();
        }
      });
    } catch (err: any) {
      setError('Failed to open Google Sign-In popup. Trying alternative method...');
      renderGoogleButton();
    }
  }, [supplierName, googleScriptLoaded]);

  const renderGoogleButton = () => {
    const container = document.getElementById('google-signin-fallback');
    if (container && window.google && window.google.accounts && window.google.accounts.id) {
      // Clear any existing content
      container.innerHTML = '';
      
      try {
        window.google.accounts.id.renderButton(container, {
          theme: 'outline',
          size: 'large',
          width: '100%',
          type: 'standard',
          text: 'signin_with',
          shape: 'rectangular'
        });
      } catch (err) {
        setError('Google Sign-In is not available. Please try email login or refresh the page.');
      }
    }
  };

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div className="login-container">
        <div className="login-box" style={{
          backgroundColor: isDarkMode ? '#1a1a1a' : 'white',
          color: isDarkMode ? '#ffffff' : '#333333',
          boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.1)'
        }}>
        {(finalLoading || isTransitioning) ? (
          // Loading Screen - Takes absolute priority during final login
          <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            <div style={{ 
              width: '48px', 
              height: '48px', 
              border: '4px solid transparent', 
              borderTop: '4px solid #007acc', 
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1.5rem'
            }}></div>
            <h2 style={{ color: isDarkMode ? '#ffffff' : '#333333' }}>Connecting to Dashboard</h2>
            
            {/* Progress Bar */}
            <div style={{ 
              width: '100%', 
              maxWidth: '400px', 
              margin: '1rem auto',
              backgroundColor: isDarkMode ? '#444444' : '#e1e8ed',
              borderRadius: '10px',
              height: '8px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${loadingProgress}%`,
                height: '100%',
                backgroundColor: '#007acc',
                borderRadius: '10px',
                transition: 'width 0.5s ease'
              }}></div>
            </div>
            
            {/* Loading Step */}
            <p style={{ color: isDarkMode ? '#b0b0b0' : '#6c757d', marginBottom: '1rem', minHeight: '1.5rem' }}>
              {loadingStep || 'Please wait while we set up your workspace...'}
            </p>
            
            <div style={{ 
              padding: '0.75rem',
              backgroundColor: isDarkMode ? '#1a2d3d' : '#e7f3ff',
              border: `1px solid ${isDarkMode ? '#2d4a5f' : '#b3d9ff'}`,
              borderRadius: '6px',
              fontSize: '0.875rem',
              color: isDarkMode ? '#87ceeb' : '#0066cc',
              maxWidth: '400px',
              margin: '0 auto'
            }}>
              ✅ Authenticated as: <strong>{authenticatedEmail}</strong><br/>
              🏢 Supplier: <strong>{supplierName}</strong><br/>
              🌍 Environment: <strong>{environment}</strong>
            </div>
          </div>
        ) : !finalLoading && currentStep === 'auth' ? (
          // Step 1: Authentication
          <>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h2 style={{ color: isDarkMode ? '#ffffff' : '#333333' }}>🔐 Reports Dashboard</h2>
              <p style={{ color: isDarkMode ? '#b0b0b0' : '#6c757d', marginBottom: '0.5rem' }}>Sign in to your account</p>
              
              {/* Environment Indicator */}
              <div style={{ 
                display: 'inline-block',
                padding: '0.25rem 0.75rem',
                backgroundColor: '#e7f3ff',
                border: '1px solid #007acc',
                borderRadius: '20px',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#0066cc',
                marginBottom: '1rem'
              }}>
                🧪 Testing Environment
              </div>
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: '0.5rem',
                fontSize: '0.875rem',
                color: '#6c757d'
              }}>
                <span style={{ 
                  backgroundColor: '#007acc', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: '20px', 
                  height: '20px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '12px'
                }}>1</span>
                <span style={{ fontWeight: '600' }}>Login</span>
                <span>→</span>
                <span style={{ 
                  backgroundColor: isDarkMode ? '#444444' : '#e1e8ed', 
                  color: isDarkMode ? '#888888' : '#6c757d', 
                  borderRadius: '50%', 
                  width: '20px', 
                  height: '20px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '12px'
                }}>2</span>
                <span>Select Supplier</span>
              </div>
            </div>

            {/* Google Sign-in Button */}
            {googleScriptLoaded ? (
              <div id="google-signin-container" style={{ marginBottom: '1rem' }}>
                <button
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    backgroundColor: loading ? '#ccc' : '#4285f4',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '500',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.75rem',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                  onMouseOver={(e) => !loading && (e.currentTarget.style.backgroundColor = '#3367d6')}
                  onMouseOut={(e) => !loading && (e.currentTarget.style.backgroundColor = '#4285f4')}
                >
                  <svg width="20" height="20" viewBox="0 0 18 18">
                    <path fill="#4285f4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
                    <path fill="#34a853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2.04a4.8 4.8 0 0 1-7.18-2.53H1.83v2.07A8 8 0 0 0 8.98 17z"/>
                    <path fill="#fbbc05" d="M4.5 10.49a4.8 4.8 0 0 1 0-3.07V5.35H1.83a8 8 0 0 0 0 7.28l2.67-2.14z"/>
                    <path fill="#ea4335" d="M8.98 4.72c1.16 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.35L4.5 7.42a4.77 4.77 0 0 1 4.48-2.7z"/>
                  </svg>
                  {loading ? 'Connecting...' : 'Sign in with Google'}
                </button>
                
                <div id="google-signin-fallback" style={{ marginTop: '0.5rem' }}></div>
              </div>
            ) : (
              <button
                disabled={true}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  backgroundColor: '#ccc',
                  color: '#666',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  marginBottom: '1rem'
                }}
              >
                <span>⏳</span>
                Loading Google Sign-In...
              </button>
            )}

            {error && (
              <div style={{ 
                marginTop: '1rem',
                padding: '0.75rem',
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '6px',
                color: '#721c24',
                fontSize: '0.875rem'
              }}>
                {error}
              </div>
            )}
          </>
        ) : (
          // Step 2: Workspace Configuration
          <>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h2 style={{ color: isDarkMode ? '#ffffff' : '#333333' }}>Reports Dashboard</h2>
              <p style={{ color: isDarkMode ? '#b0b0b0' : '#6c757d', marginBottom: '0.5rem' }}>Select your workspace to continue</p>
              
              {/* Environment Indicator */}
              <div style={{ 
                display: 'inline-block',
                padding: '0.25rem 0.75rem',
                backgroundColor: '#e7f3ff',
                border: '1px solid #007acc',
                borderRadius: '20px',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#0066cc',
                marginBottom: '1rem'
              }}>
                🧪 Testing Environment
              </div>
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: '0.5rem',
                fontSize: '0.875rem',
                color: '#6c757d'
              }}>
                <span style={{ 
                  backgroundColor: '#28a745', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: '20px', 
                  height: '20px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '12px'
                }}>✓</span>
                <span>Login</span>
                <span>→</span>
                <span style={{ 
                  backgroundColor: '#007acc', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: '20px', 
                  height: '20px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontSize: '12px'
                }}>2</span>
                <span style={{ fontWeight: '600' }}>Select Supplier</span>
              </div>
              <div style={{ 
                marginTop: '1rem',
                padding: '0.75rem',
                backgroundColor: isDarkMode ? '#1a3d2e' : '#d4edda',
                border: `1px solid ${isDarkMode ? '#2e5f47' : '#c3e6cb'}`,
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: isDarkMode ? '#90ee90' : '#155724'
              }}>
                ✅ Authenticated as: <strong>{authenticatedEmail}</strong>
              </div>
            </div>

            {/* Step 1: Select Supplier */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <h3 style={{ 
                  fontSize: '1.1rem', 
                  fontWeight: '600', 
                  margin: 0,
                  color: isDarkMode ? '#ffffff' : '#333'
                }}>
                  Step 1: Select Supplier
                </h3>
                <span style={{ 
                  fontSize: '0.75rem', 
                  color: isDarkMode ? '#b0b0b0' : '#6c757d',
                  backgroundColor: isDarkMode ? '#2a2a2a' : '#f8f9fa',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '12px'
                }}>
                  {suppliers.length} available
                </span>
              </div>
              
              {/* Last used supplier indicator - Clickable */}
              {lastUsedSupplier && (
                <div 
                  onClick={() => {
                    setSupplierName(lastUsedSupplier);
                    setSupplierSearch(lastUsedSupplier);
                    setIsSupplierDropdownOpen(false);
                  }}
                  style={{
                    fontSize: '0.875rem',
                    color: '#007acc',
                    marginBottom: '0.5rem',
                    padding: '0.5rem',
                    backgroundColor: isDarkMode ? '#1a2d3d' : '#f0f8ff',
                    border: `1px solid ${isDarkMode ? '#2d4a5f' : '#b3d9ff'}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isDarkMode ? '#2a4d6f' : '#e7f3ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isDarkMode ? '#1a2d3d' : '#f0f8ff';
                  }}
                >
                  <span>💡 Last used: <strong>{lastUsedSupplier}</strong></span>
                  <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>Click to select</span>
                </div>
              )}
              {/* Custom Searchable Dropdown */}
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder={loadingSuppliers ? 'Loading suppliers...' : 'Search and select supplier...'}
                  value={isSupplierDropdownOpen ? supplierSearch : (supplierName || supplierSearch || '')}
                  onChange={(e) => {
                    setSupplierSearch(e.target.value);
                    setIsSupplierDropdownOpen(true);
                    // Clear selected supplier when user starts typing
                    if (supplierName && e.target.value !== supplierName) {
                      setSupplierName('');
                    }
                  }}
                  onFocus={() => {
                    setIsSupplierDropdownOpen(true);
                    // If there's a selected supplier, clear search to allow new search
                    if (supplierName && !supplierSearch) {
                      setSupplierSearch(supplierName);
                    }
                  }}
                  disabled={finalLoading || loadingSuppliers}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    paddingRight: '2.5rem',
                    border: `2px solid ${isSupplierDropdownOpen ? '#007acc' : (isDarkMode ? '#444444' : '#e1e8ed')}`,
                    borderRadius: '8px',
                    fontSize: '1rem',
                    backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                    color: isDarkMode ? '#ffffff' : '#333333',
                    cursor: (finalLoading || loadingSuppliers) ? 'not-allowed' : 'text',
                    outline: 'none',
                    transition: 'border-color 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                />
                
                {/* Clear and Dropdown Icons */}
                <div style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  {(supplierName || supplierSearch) && (
                    <div
                      onClick={() => {
                        if (!finalLoading && !loadingSuppliers) {
                          setSupplierName('');
                          setSupplierSearch('');
                          setIsSupplierDropdownOpen(false);
                        }
                      }}
                      style={{
                        cursor: (finalLoading || loadingSuppliers) ? 'not-allowed' : 'pointer',
                        color: isDarkMode ? '#b0b0b0' : '#6c757d',
                        fontSize: '1rem',
                        padding: '2px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="Clear selection"
                    >
                      ✕
                    </div>
                  )}
                  <div 
                    onClick={() => !finalLoading && !loadingSuppliers && setIsSupplierDropdownOpen(!isSupplierDropdownOpen)}
                    style={{
                      cursor: (finalLoading || loadingSuppliers) ? 'not-allowed' : 'pointer',
                      color: isDarkMode ? '#b0b0b0' : '#6c757d',
                      fontSize: '1.25rem'
                    }}
                  >
                    {isSupplierDropdownOpen ? '▲' : '▼'}
                  </div>
                </div>

                {/* Dropdown List */}
                {isSupplierDropdownOpen && !loadingSuppliers && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: isDarkMode ? '#2a2a2a' : 'white',
                    border: '2px solid #007acc',
                    borderTop: 'none',
                    borderRadius: '0 0 8px 8px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: isDarkMode ? '0 4px 6px rgba(0,0,0,0.3)' : '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                    {filteredSuppliers.length > 0 ? (
                      filteredSuppliers.map((supplier) => (
                        <div
                          key={supplier._id}
                          onClick={() => {
                            setSupplierName(supplier.name);
                            setSupplierSearch(supplier.name);
                            setIsSupplierDropdownOpen(false);
                          }}
                          style={{
                            padding: '0.75rem',
                            cursor: 'pointer',
                            borderBottom: `1px solid ${isDarkMode ? '#444444' : '#e1e8ed'}`,
                            transition: 'background-color 0.2s ease',
                            backgroundColor: supplierName === supplier.name ? '#e7f3ff' : (isDarkMode ? '#2a2a2a' : 'white'),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            color: isDarkMode ? '#ffffff' : '#333333'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDarkMode ? '#3a3a3a' : '#f8f9fa'}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 
                              supplierName === supplier.name ? '#e7f3ff' : (isDarkMode ? '#2a2a2a' : 'white');
                          }}
                        >
                          <span>{supplier.name}</span>
                          {supplier.name === lastUsedSupplier && (
                            <span style={{
                              fontSize: '0.75rem',
                              color: '#007acc',
                              backgroundColor: '#e7f3ff',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '10px',
                              fontWeight: '600'
                            }}>
                              Last used
                            </span>
                          )}
                        </div>
                      ))
                    ) : (
                      <div style={{
                        padding: '0.75rem',
                        color: isDarkMode ? '#b0b0b0' : '#6c757d',
                        fontStyle: 'italic',
                        textAlign: 'center'
                      }}>
                        No suppliers found matching "{supplierSearch}"
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Click outside to close dropdown */},
              {isSupplierDropdownOpen && (
                <div 
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 999
                  }}
                  onClick={() => setIsSupplierDropdownOpen(false)}
                />
                )}
            </div>

            {/* Advanced Options (Collapsible) */}
            <details style={{ marginBottom: '1.5rem' }}>
              <summary style={{ 
                cursor: 'pointer', 
                fontWeight: '500',
                color: '#007acc',
                marginBottom: '0.75rem'
              }}>
                Advanced options ▾
              </summary>
              <div style={{ 
                padding: '1rem',
                backgroundColor: isDarkMode ? '#2a2a2a' : '#f8f9fa',
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: isDarkMode ? '#b0b0b0' : '#6c757d'
              }}>
                {/* Dark Mode Toggle */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '0.75rem'
                }}>
                  <div>
                    <div style={{ fontWeight: '500', color: isDarkMode ? '#ffffff' : '#333333', marginBottom: '0.25rem' }}>
                      🌙 Dark Mode
                    </div>
                    <div style={{ fontSize: '0.75rem' }}>
                      Toggle dark theme for better viewing in low light
                    </div>
                  </div>
                  <label style={{ 
                    position: 'relative',
                    display: 'inline-block',
                    width: '50px',
                    height: '24px',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={isDarkMode}
                      onChange={toggleDarkMode}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: isDarkMode ? '#007acc' : '#ccc',
                      borderRadius: '24px',
                      transition: '0.3s ease',
                      cursor: 'pointer'
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '',
                        height: '18px',
                        width: '18px',
                        left: isDarkMode ? '26px' : '3px',
                        bottom: '3px',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        transition: '0.3s ease'
                      }}></span>
                    </span>
                  </label>
                </div>
              </div>
            </details>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button 
                type="button"
                onClick={() => {
                  if (!finalLoading) {
                    setCurrentStep('auth');
                    setAuthenticatedEmail('');
                    setSupplierName('');
                    setError('');
                  }
                }}
                disabled={finalLoading}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  backgroundColor: finalLoading ? '#ccc' : (isDarkMode ? '#2a2a2a' : 'white'),
                  color: finalLoading ? '#666' : (isDarkMode ? '#ffffff' : '#ffffffff'),
                  border: `2px solid ${isDarkMode ? '#444444' : '#e1e8ed'}`,
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: finalLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: finalLoading ? 0.6 : 1
                }}
              >
                ← Back
              </button>
              <button
                onClick={() => {
                  // Immediate visual feedback - disable button and show loading immediately
                  if (!finalLoading && !isTransitioning && isSupplierSelected) {
                    setIsTransitioning(true);
                    handleFinalLogin();
                  }
                }}
                disabled={finalLoading || isTransitioning || !isSupplierSelected}
                style={{
                  flex: 2,
                  padding: '0.875rem',
                  backgroundColor: (isSupplierSelected && !finalLoading && !isTransitioning) ? '#007acc' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: (isSupplierSelected && !finalLoading && !isTransitioning) ? 'pointer' : 'not-allowed',
                  transition: 'background-color 0.1s ease',
                  opacity: (!isSupplierSelected && !finalLoading && !isTransitioning) ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {(finalLoading || isTransitioning) && (
                  <span style={{ 
                    width: '16px', 
                    height: '16px', 
                    border: '2px solid transparent', 
                    borderTop: '2px solid white', 
                    borderRadius: '50%', 
                    animation: 'spin 1s linear infinite' 
                  }}></span>
                )}
                {(finalLoading || isTransitioning) ? 'Loading...' : 'Continue'}
              </button>
            </div>

            {error && (
              <div style={{ 
                marginTop: '1rem',
                padding: '0.75rem',
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '6px',
                color: '#721c24',
                fontSize: '0.875rem'
              }}>
                {error}
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </>
  );
};

export default Login;
